import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import axios from "axios";

import api from "@api/index";
import { IResponseDto } from "@interfaces/rest/response.dto";
import { IGuestDto, IGuestSaveDto } from "@interfaces/rest/guestData.dto";
import { IMediaDto } from "@interfaces/rest/media.dto";
import { ISurveyAnswer } from "@pages/Survey/survey.types";
import { QueryKeyEnum } from "@interfaces/enums/queryKeys.enum";
import { SITE_SETTINGS } from "@config/constants";
import useConfig from "@providers/configProvider/useConfig";

const useUserState = (expressionsAPI?: string, providedBarcode?: string) => {
  const navigate = useNavigate();
  const { config } = useConfig();

  const overridedAxios = axios.create({
    baseURL: config?.CaptureAPI_URI,
    headers: {
      uuid: config?.deviceUuid,
      appKey: config?.appKey,
    },
  });

  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [user, setUser] = useState<IGuestDto | null>(null);
  const [activeBarcode, setActiveBarcode] = useState(providedBarcode ?? "");
  const [photos, setPhotos] = useState<IMediaDto[] | null>(null);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [isInterviewing, setIsInterviewing] = useState(false);

  const getUserData = async (barcode: string) => {
    const response = await overridedAxios.get(api.guestInfo, {
      params: { ticketBarcode: barcode },
    });
    return response.data;
  };

  const { refetch: refetchUserData, isFetching: isUserFetching } = useQuery(
    [QueryKeyEnum.getUserData, activeBarcode],
    () => getUserData(activeBarcode),
    {
      enabled: false,
      cacheTime: 0,
      onSuccess: (response) => {
        if (!response.IsSuccess && !response.data) {
          navigate(`/login?barcode=${activeBarcode}`);
          return;
        }
        setUser(response.data);
        getUserPhotosByBarcode(activeBarcode);
        navigate("/photos");
      },
    }
  );

  const saveUserMutation = useMutation(
    (data: {
      ticketBarcodes: string;
      email: string;
      zipCode: string;
      captureNewsYN: boolean;
    }) =>
      axios
        .post<IResponseDto<IGuestSaveDto>>(api.saveGuest, {
          data: {
            email: data.email,
            zipCode: data.zipCode,
            captureNewsYN: data.captureNewsYN,
          },
          ticketBarcodes: data.ticketBarcodes,
          survey: {},
        })
        .then((response) => {
          return response;
        })
        .catch((error) => {
          throw error;
        }),
    {
      onSuccess: (response) => {
        if (response.data.response?.guestId) {
          refetchUserData();
        }
      },
      onError: (error) => {
        console.error("Save user failed:", error);
      },
    }
  );

  const getUserPhotosByBarcode = async (barcode: string): Promise<void> => {
    setIsLoadingImages(true);
    try {
      const response = await axios.get<IResponseDto<IMediaDto[]>>(
        api.guestPhotos(barcode)
      );
      if (response.data.response?.length) {
        setPhotos(response.data.response);
      }
    } catch (error) {
      console.error("Error getting photos by barcode:", error);
    } finally {
      setIsLoadingImages(false);
    }
  };

  const handleSurveySubmit = async (
    answersList: ISurveyAnswer
  ): Promise<void> => {
    try {
      const questionIds = Object.keys(answersList);
      const answersDto = questionIds.map((questionId) => ({
        questionId,
        answer: answersList[questionId],
      }));

      await axios.post(api.postSurveyAnswer, {
        answers: answersDto,
        guestId: user?.guestId,
        ticketBarcode: activeBarcode,
      });

      setIsInterviewing(false);
      setUser((prevUser) =>
        prevUser
          ? {
              ...prevUser,
              guestData: {
                ...prevUser.guestData,
                passedSurvey: true,
              },
            }
          : null
      );
    } catch (error) {
      console.error("Error submiting survey:", error);
    }
  };

  const shareImageViaEmail = async (
    id: string | string[],
    email: string
  ): Promise<string> => {
    try {
      const payload = {
        emailsList: [email],
        from: user?.guestData.email,
        mediaIds: Array.isArray(id) ? id : [id],
      };

      const response = await overridedAxios.post(api.mediaShare, payload);
      setSelectedPhotos([]);
      return response.data.IsSuccess
        ? ""
        : response.data.errorMessage || response.data.data.join(", ");
    } catch (error: any) {
      return error?.message || "Unexpected error";
    }
  };

  const downloadMedia = async (mediaPath: string): Promise<void> => {
    try {
      const response = await overridedAxios.get(api.mediaDownload, {
        params: {
          mediaPath,
          email: user?.guestData.email,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      const filename = mediaPath.split("/").pop() || "download";

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading media:", error);
      throw error;
    }
  };

  const saveSiverScreenBg = async (
    mediaId: string,
    backgroundId: string
  ): Promise<boolean> => {
    try {
      const response = await axios.put(api.mediaLayer(mediaId), {
        backgroundMediaLayer: backgroundId,
      });
      return response.status === 200;
    } catch {
      return false;
    }
  };

  const resetUser = () => {
    setUser(null);
    setPhotos(null);
    setActiveBarcode("");
    setSelectedPhotos([]);
  };

  const checkIsShouldSurvey = () => {
    if (user?.guestData && !user.guestData.passedSurvey) {
      setIsInterviewing(true);
    }
  };

  const handleSurveyClose = () => {
    setIsInterviewing(false);
  };

  const getUserByBarcode = async (barcode: string) => {
    setActiveBarcode(barcode);
  };

  const saveUser = async (
    ticketBarcodes: string,
    email: string,
    zipCode: string,
    captureNewsYN: boolean
  ) => {
    saveUserMutation.mutate({
      ticketBarcodes,
      email,
      zipCode,
      captureNewsYN,
    });
  };

  const selectPhoto = (id: string, isSelecting: boolean) => {
    setSelectedPhotos((prevList) =>
      isSelecting
        ? [...prevList, id]
        : prevList.filter((listItemId) => listItemId !== id)
    );
  };

  const selectAllPhotos = () => {
    if (photos) {
      setSelectedPhotos(photos.map((photo) => photo.mediaId));
    }
  };

  const selectSinglePhoto = (id: string) => setSelectedPhotos([id]);

  const clearSelectedPhotos = () => setSelectedPhotos([]);

  const markPhotoAsUsed = (mediaId: string, mediaLayerId: string) => {
    setPhotos((prev) => {
      try {
        const itemIndex = prev!.findIndex((item) => item.mediaId === mediaId);
        const item = prev![itemIndex] as IMediaDto;
        const itemLayerIndex = item!.mediaLayers!.findIndex(
          (layer) => layer.mediaLayerId === mediaLayerId
        );
        const itemLayer = item.mediaLayers![itemLayerIndex];
        const newItemLayer = { ...itemLayer, isUsed: true };
        const newLayersArray = item.mediaLayers!.splice(
          itemLayerIndex,
          1,
          newItemLayer
        );
        prev!.splice(itemIndex, 1, { ...item, mediaLayers: newLayersArray });

        return prev;
      } catch (error) {
        return prev;
      }
    });
  };

  useEffect(() => {
    if (providedBarcode?.length === SITE_SETTINGS.BARCODE_LENGTH) {
      setActiveBarcode(providedBarcode);
      refetchUserData();
    } else if (activeBarcode.length === SITE_SETTINGS.BARCODE_LENGTH) {
      refetchUserData();
    }
  }, [providedBarcode, activeBarcode, refetchUserData]);

  return {
    isLoading: saveUserMutation.isLoading || isUserFetching,
    isLoadingImages,
    getUserByBarcode,
    user,
    saveUser,
    resetUser,
    getUserPhotosByBarcode,
    photos,
    shareImageViaEmail,
    downloadMedia,
    saveSiverScreenBg,
    activeBarcode,
    selectedPhotos,
    selectPhoto,
    selectSinglePhoto,
    selectAllPhotos,
    clearSelectedPhotos,
    isInterviewing,
    checkIsShouldSurvey,
    handleSurveyClose,
    handleSurveySubmit,
    markPhotoAsUsed,
  };
};

export default useUserState;
